import { CSSProperties } from 'react'
import { Input, Select, Tooltip } from 'antd'
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons'
import { useRowsNColumns } from '@components/imagemap/right-panel/hooks/useRowsNColumns'
import './ReportRefine.less'

export const isReady = (item: any) =>
  item.kind === 'slide' && item.tableId != null && item.rows?.length > 0 && item.columns?.length > 0 && item.layout

const note = (item: any) => [...(item.errors ?? []), item.reason].filter(Boolean).join(' · ')

const SlideCard = ({ item, label, order, tables, layouts, onChange }) => {
  const { rows, columns, loading } = useRowsNColumns(item.tableId ?? null)
  const ready = isReady(item)
  const set = (change: any) => onChange({ ...item, ...change })
  return (
    <div className={`refine-card${ready ? '' : ' is-missing'}`} style={{ '--i': order } as CSSProperties}>
      <div className="refine-card__head">
        <span className="refine-card__number">{label}</span>
        <Input
          className="refine-card__headline"
          bordered={false}
          value={item.headline ?? item.title}
          onChange={e => set({ headline: e.target.value })}
          aria-label={`Headline of slide ${label}`}
        />
        <Tooltip title={note(item) || null}>
          <span className={`refine-card__status ${ready ? 'is-ready' : 'is-missing'}`}>
            {ready ? <CheckCircleFilled /> : <ExclamationCircleFilled />}
            {ready ? 'Ready' : 'Needs data'}
          </span>
        </Tooltip>
      </div>
      <div className="refine-card__topic">Topic: {item.title}</div>
      <div className="refine-card__fields">
        <label>
          <span>Table</span>
          <Select
            showSearch
            placeholder="Choose a table"
            value={item.tableId ?? undefined}
            optionFilterProp="label"
            options={tables.map(t => ({ value: t.id, label: t.name }))}
            onChange={tableId => set({ tableId, rows: [], columns: [] })}
            getPopupContainer={node => node.parentElement}
          />
        </label>
        <label>
          <span>Layout</span>
          <Select
            placeholder="Choose a layout"
            value={item.layout ?? undefined}
            options={layouts.map(l => ({ value: l.key, label: l.name }))}
            onChange={layout => set({ layout })}
            getPopupContainer={node => node.parentElement}
          />
        </label>
        <label>
          <span>Rows (categories)</span>
          <Select
            mode="multiple"
            placeholder="Choose rows"
            loading={loading}
            disabled={item.tableId == null}
            value={item.rows ?? []}
            optionFilterProp="label"
            options={rows.map((r, i) => ({ value: i, label: r.name }))}
            onChange={value => set({ rows: value })}
            maxTagCount="responsive"
            maxTagTextLength={28}
            getPopupContainer={node => node.parentElement}
          />
        </label>
        <label>
          <span>Columns (series)</span>
          <Select
            mode="multiple"
            placeholder="Choose columns"
            loading={loading}
            disabled={item.tableId == null}
            value={item.columns ?? []}
            optionFilterProp="label"
            options={columns.map((c, i) => ({ value: i, label: c.name }))}
            onChange={value => set({ columns: value })}
            maxTagCount="responsive"
            maxTagTextLength={28}
            getPopupContainer={node => node.parentElement}
          />
        </label>
      </div>
    </div>
  )
}

export const ReportRefine = ({ items, tables, layouts, onChange }) => {
  const dataLayouts = layouts.filter(l => l.kind === 'data')
  const slides = items.filter(i => i.kind === 'slide')
  const ready = slides.filter(isReady).length
  let section = 0
  let slide = 0
  let order = 0
  return (
    <div className="refine">
      <div className="refine__summary">
        <div className="refine__count">
          <span>
            <strong>{ready}</strong> of {slides.length} slides ready
          </span>
          <div className="refine__bar">
            <span style={{ width: `${slides.length ? (ready / slides.length) * 100 : 0}%` }} />
          </div>
        </div>
        {ready < slides.length && <p>Slides that still need data are left out of the presentation.</p>}
        {!dataLayouts.length && (
          <p>The template has no slide with exactly one chart, so no data layout is available.</p>
        )}
      </div>
      {items.map(item => {
        if (item.kind === 'section') {
          section += 1
          slide = 0
          return (
            <div key={item.key} className="refine__section" style={{ '--i': Math.min(order++, 14) } as CSSProperties}>
              <span className="refine__badge">Section {section}</span>
              {item.title}
            </div>
          )
        }
        slide += 1
        return (
          <SlideCard
            key={item.key}
            item={item}
            label={section ? `${section}.${slide}` : String(slide)}
            order={Math.min(order++, 14)}
            tables={tables}
            layouts={dataLayouts}
            onChange={(changed: any) => onChange(items.map(i => (i.key === item.key ? changed : i)))}
          />
        )
      })}
    </div>
  )
}
